import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ResponsiveModal} from "@pg-design/modal-module";
import {Popover} from "@pg-design/notifications";
import {FormProviderRHF} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {MULTILEAD_RESPONSIVE_MODAL_TEST_IDS, OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {getAmplitudeDisplayedCtaText} from "../../../tracking/amplitude/get_amplitude_displayed_cta_text";
import {leadModalZindex} from "../../constants/lead/lead_modal_zindex";
import {useApplicationMultiLead} from "../../hooks/multi_lead/use_application_multi_lead";
import {useApplicationMultiLeadResponsiveForm} from "../../hooks/multi_lead/use_application_multi_lead_responsive_form";
import {useApplicationMultiLeadSimpleRecommendations} from "../../hooks/multi_lead/use_application_multi_lead_simple_recommendations";
import {useApplicationMultiLeadSwitchViewFocus} from "../../hooks/multi_lead/use_application_multi_lead_switch_view_focus";
import {IApplicationMultiLeadProps} from "../../types/multi_lead/IApplicationMultiLeadProps";
import {ApplicationMultiLeadResponsiveModalPropertyList} from "./ApplicationMultiLeadResponsiveModalPropertyList";
import {ApplicationMultiLeadResponsiveModalUserDataFields} from "./ApplicationMultiLeadResponsiveModalUserDataFields";

const multiLeadMapPinsImage = require("../../images/multi_lead/multi_lead_map_pins.svg");

const SEND_BUTTON_TEXT = "Wyślij";

interface IProps extends IApplicationMultiLeadProps {
    titleId: string;
    descriptionId: string;
}

export const ApplicationMultiLeadResponsiveModalContent = (props: IProps) => {
    const {
        titleId,
        descriptionId,
        handleModalClose,
        handleSetMultiLeadSummaryStep,
        originalApplicationFormValues,
        originalApplicationResponse,
        recommendationType,
        recommendationsWithPriceFilter,
        showOriginalOffer,
        sourceSection,
        customSourceSection,
        customGtmTracking,
        disableDefaultSelection,
        amplitudeAuthPlacement
    } = props;

    const {onSuccessfulSubmit, onCloseClick} = useApplicationMultiLead({
        recommendationType,
        handleModalClose,
        handleSetMultiLeadSummaryStep,
        customGtmTracking
    });
    const {
        applicationData,
        originalApplicationGeoPoint,
        originalApplicationCountry,
        recommendedProperties,
        showPropertiesList,
        setShowPropertiesList,
        isMultiLeadSubmitting,
        applicationsApiError,
        selectedRecommendations,
        isSendApplicationDisabled,
        emailNotificationProps,
        initialValues,
        submitApplications,
        handleChange,
        handlePinClick,
        onSendClick,
        handleClose
    } = useApplicationMultiLeadSimpleRecommendations({
        originalApplicationFormValues,
        recommendationsWithPriceFilter,
        showOriginalOffer,
        sourceSection,
        onModalClose: onCloseClick,
        onSuccessfulSubmit,
        recommendationType,
        customSourceSection,
        originalApplicationResponse,
        customGtmTracking,
        disableDefaultSelection,
        amplitudeAuthPlacement,
        amplitudeCtaText: getAmplitudeDisplayedCtaText(SEND_BUTTON_TEXT),
        sendButtonText: SEND_BUTTON_TEXT
    });

    const {formMethods, isUserDataValid, handleValidSubmit} = useApplicationMultiLeadResponsiveForm({initialValues, submitApplications});
    const {propertyListRef, showPropertiesListButtonRef, switchView} = useApplicationMultiLeadSwitchViewFocus({showPropertiesList, setShowPropertiesList});

    return (
        <FormProviderRHF {...formMethods}>
            <form className={formStyle} onSubmit={formMethods.handleSubmit(handleValidSubmit)}>
                <ResponsiveModal.Header divider={false} className={headerStyle}>
                    <img src={multiLeadMapPinsImage} alt="" width={72} height={72} className={illustrationStyle} />

                    <div className={headerTextStyle}>
                        <Text as="h2" id={titleId} variant="headline_4" color="var(--colors-secondary)">
                            Sprawdź nasze propozycje dla Ciebie
                        </Text>
                        <Text as="p" id={descriptionId} variant="body_copy_2" color="var(--colors-secondary)" className={descriptionStyle}>
                            Zaznacz oferty, które wpadły Ci w oko i dowiedz się o nich więcej od dewelopera.
                        </Text>
                    </div>

                    <ResponsiveModal.CloseButton onClick={handleClose} dataTestId={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.CLOSE_BUTTON} />
                </ResponsiveModal.Header>

                <ResponsiveModal.Body className={showPropertiesList ? listBodyStyle : formBodyStyle}>
                    {showPropertiesList ? (
                        <div ref={propertyListRef}>
                            <ApplicationMultiLeadResponsiveModalPropertyList
                                mainPropertyId={applicationData?.property?.detail?.id}
                                allowToSelectMainProperty={showOriginalOffer}
                                recommendedProperties={recommendedProperties}
                                selectedPropertyIds={selectedRecommendations}
                                handleChange={handleChange}
                                handlePinClick={handlePinClick}
                                originalApplicationGeoPoint={originalApplicationGeoPoint}
                                originalApplicationCountry={originalApplicationCountry}
                            />
                        </div>
                    ) : (
                        <>
                            <button
                                ref={showPropertiesListButtonRef}
                                type="button"
                                className={textButtonStyle}
                                onClick={() => switchView(true)}
                                data-testid={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.SHOW_RECOMMENDATIONS_BUTTON}
                            >
                                <Text as="span" variant="body_copy_2" color="var(--colors-secondary)">
                                    Wybierz dodatkowe oferty
                                </Text>
                            </button>
                            <div>
                                <ApplicationMultiLeadResponsiveModalUserDataFields emailNotificationProps={emailNotificationProps} />
                            </div>
                        </>
                    )}
                </ResponsiveModal.Body>

                <ResponsiveModal.Footer className={footerStyle}>
                    <Popover
                        arrow
                        isActive={isSendApplicationDisabled}
                        popoverPlace="top"
                        body="Zaznacz propozycje powyżej aby wysłać zapytanie"
                        toggleHoverHandlers
                        toggleOnClickOutside
                        toggleClickHandler
                        zIndex={leadModalZindex}
                    >
                        <div className={footerContentStyle}>
                            {applicationsApiError ? (
                                <Text variant="info_txt_3" as="p" color="var(--colors-danger)" align="center">
                                    {applicationsApiError}
                                </Text>
                            ) : null}
                            {!isUserDataValid && showPropertiesList && !applicationsApiError ? (
                                <Text
                                    variant="info_txt_3"
                                    as="p"
                                    color="var(--colors-danger)"
                                    align="center"
                                    dataTestId={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.INVALID_DATA_MESSAGE}
                                >
                                    Nie można wysłać zapytań. Sprawdź poprawność danych{" "}
                                    <button
                                        type="button"
                                        className={inlineTextButtonStyle}
                                        onClick={() => switchView(false)}
                                        data-testid={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.SHOW_USER_DATA_FORM_BUTTON}
                                    >
                                        klikając tu.
                                    </button>
                                </Text>
                            ) : null}
                            <Button
                                type="submit"
                                variant="filled_primary"
                                size="medium"
                                fullWidth
                                disabled={isSendApplicationDisabled || isMultiLeadSubmitting || !isUserDataValid}
                                isLoading={formMethods.formState.isSubmitting || isMultiLeadSubmitting}
                                onClick={onSendClick}
                                dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                            >
                                {SEND_BUTTON_TEXT}
                            </Button>
                        </div>
                    </Popover>
                </ResponsiveModal.Footer>
            </form>
        </FormProviderRHF>
    );
};

const formStyle = css({
    display: "contents"
});

const headerStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1rem",
    px: 2,
    py: 1.5
});

const illustrationStyle = css({
    display: "block",
    flexShrink: 0,
    width: "7.2rem",
    height: "7.2rem"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    flex: "1 1 auto",
    minWidth: 0
});

const descriptionStyle = css({
    lineHeight: "1.8rem",
    md: {
        lineHeight: "2rem"
    }
});

const listBodyStyle = css({
    md: {
        px: 3
    }
});

const formBodyStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 2,
    px: 2,
    pb: 2,
    md: {
        px: 3
    }
});

const textButtonStyle = css({
    alignSelf: "flex-start",
    p: 0,
    border: "none",
    backgroundColor: "transparent",
    textDecoration: "underline",
    cursor: "pointer"
});

const inlineTextButtonStyle = css({
    display: "inline",
    p: 0,
    border: "none",
    backgroundColor: "transparent",
    color: "inherit",
    font: "inherit",
    textDecoration: "underline",
    cursor: "pointer"
});

const footerStyle = css({
    px: 2,
    py: 1.5,
    md: {
        px: 3,
        py: 3
    }
});

const footerContentStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1
});
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ResponsiveModal} from "@pg-design/modal-module";
import {Popover} from "@pg-design/notifications";
import {FormProviderRHF} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {MULTILEAD_RESPONSIVE_MODAL_TEST_IDS, OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {getAmplitudeDisplayedCtaText} from "../../../tracking/amplitude/get_amplitude_displayed_cta_text";
import {leadModalZindex} from "../../constants/lead/lead_modal_zindex";
import {useApplicationMultiLead} from "../../hooks/multi_lead/use_application_multi_lead";
import {useApplicationMultiLeadResponsiveForm} from "../../hooks/multi_lead/use_application_multi_lead_responsive_form";
import {useApplicationMultiLeadSimpleRecommendations} from "../../hooks/multi_lead/use_application_multi_lead_simple_recommendations";
import {useApplicationMultiLeadSwitchViewFocus} from "../../hooks/multi_lead/use_application_multi_lead_switch_view_focus";
import {IApplicationMultiLeadProps} from "../../types/multi_lead/IApplicationMultiLeadProps";
import {ApplicationMultiLeadResponsiveModalPropertyList} from "./ApplicationMultiLeadResponsiveModalPropertyList";
import {ApplicationMultiLeadResponsiveModalUserDataFields} from "./ApplicationMultiLeadResponsiveModalUserDataFields";

const multiLeadMapPinsImage = require("../../images/multi_lead/multi_lead_map_pins.svg");

const SEND_BUTTON_TEXT = "Wyślij";

interface IProps extends IApplicationMultiLeadProps {
    titleId: string;
    descriptionId: string;
}

export const ApplicationMultiLeadResponsiveModalContent = (props: IProps) => {
    const {
        titleId,
        descriptionId,
        handleModalClose,
        handleSetMultiLeadSummaryStep,
        originalApplicationFormValues,
        originalApplicationResponse,
        recommendationType,
        recommendationsWithPriceFilter,
        showOriginalOffer,
        sourceSection,
        customSourceSection,
        customGtmTracking,
        disableDefaultSelection,
        amplitudeAuthPlacement
    } = props;

    const {onSuccessfulSubmit, onCloseClick} = useApplicationMultiLead({
        recommendationType,
        handleModalClose,
        handleSetMultiLeadSummaryStep,
        customGtmTracking
    });
    const {
        applicationData,
        originalApplicationGeoPoint,
        originalApplicationCountry,
        recommendedProperties,
        showPropertiesList,
        setShowPropertiesList,
        isMultiLeadSubmitting,
        applicationsApiError,
        selectedRecommendations,
        isSendApplicationDisabled,
        emailNotificationProps,
        initialValues,
        submitApplications,
        handleChange,
        handlePinClick,
        onSendClick,
        handleClose
    } = useApplicationMultiLeadSimpleRecommendations({
        originalApplicationFormValues,
        recommendationsWithPriceFilter,
        showOriginalOffer,
        sourceSection,
        onModalClose: onCloseClick,
        onSuccessfulSubmit,
        recommendationType,
        customSourceSection,
        originalApplicationResponse,
        customGtmTracking,
        disableDefaultSelection,
        amplitudeAuthPlacement,
        amplitudeCtaText: getAmplitudeDisplayedCtaText(SEND_BUTTON_TEXT),
        sendButtonText: SEND_BUTTON_TEXT
    });

    const {formMethods, isUserDataValid, handleValidSubmit} = useApplicationMultiLeadResponsiveForm({initialValues, submitApplications});
    const {propertyListRef, showPropertiesListButtonRef, switchView} = useApplicationMultiLeadSwitchViewFocus({showPropertiesList, setShowPropertiesList});

    return (
        <FormProviderRHF {...formMethods}>
            <form className={formStyle} onSubmit={formMethods.handleSubmit(handleValidSubmit)}>
                <ResponsiveModal.Header divider={false} className={headerStyle}>
                    <img src={multiLeadMapPinsImage} alt="" width={72} height={72} className={illustrationStyle} />

                    <div className={headerTextStyle}>
                        <Text as="h2" id={titleId} variant="headline_4" color="var(--colors-secondary)">
                            Sprawdź nasze propozycje dla Ciebie
                        </Text>
                        <Text as="p" id={descriptionId} variant="body_copy_2" color="var(--colors-secondary)" className={descriptionStyle}>
                            Zaznacz oferty, które wpadły Ci w oko i dowiedz się o nich więcej od dewelopera.
                        </Text>
                    </div>

                    <ResponsiveModal.CloseButton onClick={handleClose} dataTestId={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.CLOSE_BUTTON} />
                </ResponsiveModal.Header>

                <ResponsiveModal.Body className={showPropertiesList ? listBodyStyle : formBodyStyle}>
                    {showPropertiesList ? (
                        <div ref={propertyListRef}>
                            <ApplicationMultiLeadResponsiveModalPropertyList
                                mainPropertyId={applicationData?.property?.detail?.id}
                                allowToSelectMainProperty={showOriginalOffer}
                                recommendedProperties={recommendedProperties}
                                selectedPropertyIds={selectedRecommendations}
                                handleChange={handleChange}
                                handlePinClick={handlePinClick}
                                originalApplicationGeoPoint={originalApplicationGeoPoint}
                                originalApplicationCountry={originalApplicationCountry}
                            />
                        </div>
                    ) : (
                        <>
                            <button
                                ref={showPropertiesListButtonRef}
                                type="button"
                                className={textButtonStyle}
                                onClick={() => switchView(true)}
                                data-testid={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.SHOW_RECOMMENDATIONS_BUTTON}
                            >
                                <Text as="span" variant="body_copy_2" color="var(--colors-secondary)">
                                    Wybierz dodatkowe oferty
                                </Text>
                            </button>
                            <div>
                                <ApplicationMultiLeadResponsiveModalUserDataFields emailNotificationProps={emailNotificationProps} />
                            </div>
                        </>
                    )}
                </ResponsiveModal.Body>

                <ResponsiveModal.Footer className={footerStyle}>
                    <Popover
                        arrow
                        isActive={isSendApplicationDisabled}
                        popoverPlace="top"
                        body="Zaznacz propozycje powyżej aby wysłać zapytanie"
                        toggleHoverHandlers
                        toggleOnClickOutside
                        toggleClickHandler
                        zIndex={leadModalZindex}
                    >
                        <div className={footerContentStyle}>
                            {applicationsApiError ? (
                                <Text variant="info_txt_3" as="p" color="var(--colors-danger)" align="center">
                                    {applicationsApiError}
                                </Text>
                            ) : null}
                            {!isUserDataValid && showPropertiesList && !applicationsApiError ? (
                                <Text
                                    variant="info_txt_3"
                                    as="p"
                                    color="var(--colors-danger)"
                                    align="center"
                                    dataTestId={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.INVALID_DATA_MESSAGE}
                                >
                                    Nie można wysłać zapytań. Sprawdź poprawność danych{" "}
                                    <button
                                        type="button"
                                        className={inlineTextButtonStyle}
                                        onClick={() => switchView(false)}
                                        data-testid={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.SHOW_USER_DATA_FORM_BUTTON}
                                    >
                                        klikając tu.
                                    </button>
                                </Text>
                            ) : null}
                            <Button
                                type="submit"
                                variant="filled_primary"
                                size="medium"
                                fullWidth
                                disabled={isSendApplicationDisabled || isMultiLeadSubmitting || !isUserDataValid}
                                isLoading={formMethods.formState.isSubmitting || isMultiLeadSubmitting}
                                onClick={onSendClick}
                                dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                            >
                                {SEND_BUTTON_TEXT}
                            </Button>
                        </div>
                    </Popover>
                </ResponsiveModal.Footer>
            </form>
        </FormProviderRHF>
    );
};

const formStyle = css({
    display: "contents"
});

const headerStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1rem",
    px: 2,
    py: 1.5
});

const illustrationStyle = css({
    display: "block",
    flexShrink: 0,
    width: "7.2rem",
    height: "7.2rem"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    flex: "1 1 auto",
    minWidth: 0
});

const descriptionStyle = css({
    lineHeight: "1.8rem",
    md: {
        lineHeight: "2rem"
    }
});

const listBodyStyle = css({
    md: {
        px: 3
    }
});

const formBodyStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 2,
    px: 2,
    pb: 2,
    md: {
        px: 3
    }
});

const textButtonStyle = css({
    alignSelf: "flex-start",
    p: 0,
    border: "none",
    backgroundColor: "transparent",
    textDecoration: "underline",
    cursor: "pointer"
});

const inlineTextButtonStyle = css({
    display: "inline",
    p: 0,
    border: "none",
    backgroundColor: "transparent",
    color: "inherit",
    font: "inherit",
    textDecoration: "underline",
    cursor: "pointer"
});

const footerStyle = css({
    px: 2,
    py: 1.5,
    md: {
        px: 3,
        py: 3
    }
});

const footerContentStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1
});
